import React from 'react';
import I18n from 'i18n-js';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { AppleAppStoreLink, GooglePlayStoreLink } from './shared/app_store_links';
import FooterLocalePicker from './footer_locale_picker';
import AppInstallQRCode from './app_install_qr_code';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { isEU } from '../lib/geo';
import { RCIcon } from '@reverbdotcom/commons/src/rc/RCIcon';
import Instagram from '@reverbdotcom/commons/design-system/icons/core/instagram.svg?component';
import FacebookAlt from '@reverbdotcom/commons/design-system/icons/core/facebook-alt.svg?component';
import YoutubePlay from '@reverbdotcom/commons/design-system/icons/core/youtube-play.svg?component';
import Twitter from '@reverbdotcom/commons/design-system/icons/core/twitter.svg?component';
import Pinterest from '@reverbdotcom/commons/design-system/icons/core/pinterest.svg?component';

interface IAppIconsProps {
  userLocale: string;
}

function AppIcons(props: IAppIconsProps) {
  return (
    <div className="site-subfooter__app-icons">
      <div className="site-subfooter__app-icon">
        <AppleAppStoreLink linkLocation="footer" userLocale={props.userLocale} />
      </div>
      <div className="site-subfooter__app-icon">
        <GooglePlayStoreLink linkLocation="footer" userLocale={props.userLocale} />
      </div>
      <div className="site-subfooter__fineprint">
        {I18n.t('discovery.siteSubFooter.fineprint')}
      </div>
    </div>
  );
}

function SocialIcons() {
  return (
    <ul className="site-subfooter__social-icons">
      <li className="site-subfooter__social-icons-list-item">
        <a
          href="https://www.instagram.com/reverb/"
          className="site-subfooter__social-icon"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
        >
          <RCIcon svgComponent={Instagram} inline />
        </a>
      </li>
      <li className="site-subfooter__social-icons-list-item">
        <FacebookSocialIcon />
      </li>
      <li className="site-subfooter__social-icons-list-item">
        <a
          href="https://www.youtube.com/user/reverbmarket"
          className="site-subfooter__social-icon"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="YouTube"
        >
          <RCIcon svgComponent={YoutubePlay} inline />
        </a>
      </li>
      <li className="site-subfooter__social-icons-list-item">
        <a
          href="https://twitter.com/reverb"
          className="site-subfooter__social-icon"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Twitter"
        >
          <RCIcon svgComponent={Twitter} inline />
        </a>
      </li>
      <li className="site-subfooter__social-icons-list-item">
        <a
          href="https://www.pinterest.com/reverbmarket/"
          className="site-subfooter__social-icon"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Pinterest"
        >
          <RCIcon svgComponent={Pinterest} inline />
        </a>
      </li>
    </ul>
  );
}

function FacebookSocialIcon() {
  const facebookURL = findFacebookURL(useUser());
  return (
    <a
      href={facebookURL}
      className="site-subfooter__social-icon"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Facebook"
    >
      <RCIcon svgComponent={FacebookAlt} inline />
    </a>
  );
}

export function findFacebookURL({ countryCode }: Partial<IUser>) {
  if (['GB', 'IE'].includes(countryCode)) {
    return 'https://www.facebook.com/ReverbUKandIE';
  }
  if (isEU(countryCode)) {
    return 'https://www.facebook.com/ReverbEurope';
  }

  return 'https://www.facebook.com/reverbmarket';
}

export function SiteSubFooter() {
  const { locale } = useUser();

  return (
    <div className="site-subfooter">
      <div className="site-wrapper">
        <div className="d-flex fx-align-center tablet-d-block">
          <div>
            <FooterLocalePicker />
          </div>
          <div className="site-subfooter__icon-messages">
            <div
              className="site-subfooter__icon-message site-subfooter__icon-message--gives"
            >
              <div>
                <h4>
                  {I18n.t('discovery.siteSubFooter.givesTitle')}
                </h4>
                <p>
                  {I18n.t('discovery.siteSubFooter.givesDescription')}
                </p>
              </div>
            </div>
            <div
              className="site-subfooter__icon-message site-subfooter__icon-message--carbon"
            >
              <div>
                <h4>
                  {I18n.t('discovery.siteSubFooter.carbonTitle')}
                </h4>
                <p>
                  {I18n.t('discovery.siteSubFooter.carbonDescription')}
                </p>
              </div>
            </div>
          </div>
          <div>
            <SocialIcons />
            <AppIcons userLocale={locale} />
            <div className="mtb-1 mlr-auto width-rem-30">
              <AppInstallQRCode />
            </div>
          </div>
        </div>
      </div>
      <div className="site-wrapper size-90 mt-1 tablet-align-center">
        <ul>
          <li className="d-inline-block mr-1">
            {I18n.t('discovery.siteSubFooter.copyright', { year: new Date().getFullYear() })}
          </li>
          <li className="d-inline-block mr-1">
            <a href="/page/terms" className="text-link">
              {I18n.t('discovery.siteSubFooter.terms')}
            </a>
          </li>
          <li className="d-inline-block">
            <a href="/page/privacy" className="text-link">
              {I18n.t('discovery.siteSubFooter.privacy')}
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default SiteSubFooter;
